import { useEffect, useReducer, useRef } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { focusIfShown } from "@/lib/focus";

// OP-036: modal confirmation of a destructive action. Nothing is sent before the confirm button; closing the
// dialog by any other way (Annuler, Escape, outside click) sends nothing. `onConfirm` receives no argument: the caller keeps the
// exact item the dialog describes, so the request matches what was shown. It may return the action's promise.
//
// Focus (F-NAV-1): the dialog is opened without a Radix trigger, so, as GlobalSearch does, it remembers the control
// that had focus when it opened and gives focus back to it when it closes (Escape, Annuler or confirmation), if that
// control is still shown. `contextKey` is the caller's read key (account, workspace, role and resource): once it has
// changed, or once this dialog is unmounted (another page), focus is left alone. `restoreFocusFallback` is the
// caller's surviving destination, used when the opening control is unknown or gone at close time, or when the
// confirmed action removes it (a deleted note, an unpinned card) and focus has fallen back to the document.
export default function ConfirmAction({ open, title, children, confirmLabel, onConfirm, onClose, testid, contextKey, restoreFocusFallback }) {
  const opener = useRef(null);
  const openedIn = useRef(undefined);
  const context = useRef(contextKey);
  context.current = contextKey;
  const fallback = useRef(restoreFocusFallback);
  fallback.current = restoreFocusFallback;
  const mounted = useRef(false);
  // D10: the context the dialog was opened in. A dialog never confirms in another context than the one it describes:
  // if the context changes while it is open (another workspace, account or role), it closes without sending anything.
  const shown = useRef(null);
  if (!open) shown.current = null;
  else if (!shown.current) shown.current = { key: contextKey };
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (open && shown.current && shown.current.key !== contextKey) closeRef.current?.();
  }, [open, contextKey]);
  // A confirmed action whose opening control may disappear when its result is rendered.
  const watched = useRef(null);
  const [, settled] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; watched.current = null; };
  }, []);

  const sameContext = (key) => mounted.current && key === context.current;
  const focusLost = () => !document.activeElement || document.activeElement === document.body;

  // Runs after every render of the caller, hence after the render that removes an item.
  useEffect(() => {
    const w = watched.current;
    if (!w) return;
    if (!sameContext(w.key)) { watched.current = null; return; }
    if (!w.target?.isConnected) {
      watched.current = null;
      if (focusLost()) fallback.current?.();
      return;
    }
    if (w.done) watched.current = null;
  });

  const confirm = (event) => {
    if (shown.current && shown.current.key !== context.current) {
      event?.preventDefault?.();
      closeRef.current?.();
      return undefined;
    }
    const w = { target: opener.current, key: openedIn.current, done: false };
    watched.current = w;
    let result;
    try {
      result = onConfirm?.();
    } finally {
      // The re-render after the action settles lets the effect above see whether the control survived.
      Promise.resolve(result).catch(() => {}).then(() => {
        if (watched.current === w) { w.done = true; settled(); }
      });
    }
    return result;
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <AlertDialogContent
        data-testid={testid}
        onOpenAutoFocus={() => { opener.current = document.activeElement; openedIn.current = context.current; }}
        onCloseAutoFocus={(event) => {
          const target = opener.current;
          opener.current = null;
          event.preventDefault();
          if (!sameContext(openedIn.current)) return;
          if (target && target !== document.body && focusIfShown(target)) return;
          fallback.current?.();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground" data-testid={`${testid}-text`}>{children}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} data-testid={`${testid}-confirm`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// What a follow-up holds, in words, for a confirmation text.
export function describeFollowUp(fu) {
  if (!fu) return "";
  const state = fu.status === "done" ? "terminé" : "ouvert";
  return `responsable : ${fu.owner_name || "aucun"}, échéance : ${fu.due_date || "aucune"}, état : ${state}`;
}
